import { AddGuideCommand, DeleteGuideCommand, MoveGuideCommand, RelocateGuideCommand } from "@/commands/guideCommands";
import { AddPageCommand, DeletePageCommand, DuplicatePageCommand, ImportComponentsPageCommand, duplicatePage as clonePage, RenamePageCommand } from "@/commands/pageCommands";
import {
  DEFAULT_DOCUMENT_VIEWPORT,
  createBlankDocument,
  createPage,
  duplicatePageName,
  fallbackActivePageIdAfterDelete,
  findSceneNode,
  nextPageName,
  type CurvaDocument,
  type DocumentViewport,
} from "@/document/model/document";
import type { Guide, GuideScope, GuideSelection } from "@/document/model/guides";
import { ImportScriptsFolderCommand, ReplaceScriptLibraryCommand } from "@/commands/scriptLibraryCommands";
import {
  AddEditorScriptsCommand,
  ClearEditorScriptsCommand,
  CopyEditorScriptEntryToDocumentCommand,
  ReplaceEditorScriptsCommand,
} from "@/commands/editorScriptsCommands";
import { prepareComponentImport } from "@/document/operations/componentImport";
import { prepareScriptImport, prepareScriptLibraryReplace } from "@/document/operations/scriptImport";
import { listDocumentScripts } from "@/document/model/documentScripts";
import { EDITOR_SCRIPTS_DOCUMENT_NAME } from "@/io/editorScriptsPersistence";
import { renderDocumentThumbnailPngBytes } from "@/document/previews/documentThumbnail";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { exportFramesToPdfBlob } from "@/export/pdfExporter";
import { CURVA_FILE_EXTENSION, CURVA_MIME_TYPE } from "@/io/fileTypes";
import { deserializeCurvaPackage, serializeCurvaPackage } from "@/io/package";
import { exportBytes, saveDocumentBytes, type OpenedFile } from "@/io/platform";
import type { EditorCommandHost } from "../editorCommandHost";

export interface DocumentResetOptions {
  resetHistory: () => void;
  setViewport: (viewport: DocumentViewport) => void;
  resetScopedState: () => void;
  centerViewportOnDocumentPoint: (x: number, y: number) => void;
}

export interface CurvaImportOptions extends DocumentResetOptions {
  resolveFonts: (document: CurvaDocument) => Promise<unknown>;
  notifyFontsResolved: () => void;
}

export interface ComponentImportOptions {
  componentImportInProgress: boolean;
  centerViewportOnDocumentPoint: (x: number, y: number) => void;
  refreshComponentThumbnail: (document: CurvaDocument, componentId: string) => Promise<void>;
}

export interface GuideHandlerOptions {
  onSelectedGuideChange?: (selection: GuideSelection | undefined) => void;
}

export function pageSwitchSelection(host: EditorCommandHost, pageId: string) {
  return host.selectionSnapshot({
    activePageId: pageId,
    selectedNodeIds: [],
    primarySelectedNodeId: undefined,
    mode: "select",
    selectedVertexId: undefined,
    selectedVertexIds: [],
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
}

export function newDocument(host: EditorCommandHost, options: DocumentResetOptions): void {
  const nextDocument = createBlankDocument();
  options.setViewport({ ...DEFAULT_DOCUMENT_VIEWPORT });
  options.resetHistory();
  host.commit(nextDocument, "New document");
  host.applySelectionSnapshot(pageSwitchSelection(host, nextDocument.pages[0]!.id));
  selectionStore.setActiveTool("select");
  selectionStore.setSelectedShapeKind("rectangle");
  options.resetScopedState();
  options.centerViewportOnDocumentPoint(0, 0);
}

export async function importCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
  options: CurvaImportOptions,
): Promise<void> {
  if (!file) {
    return;
  }

  uiStore.setDocumentLoading(true);
  try {
    const imported = deserializeCurvaPackage(file.bytes).document;
    const nextDocument = {
      ...imported,
      name: sanitizeDocumentBaseName(file.name.endsWith(CURVA_FILE_EXTENSION) ? file.name : imported.name),
    };
    options.setViewport({ ...DEFAULT_DOCUMENT_VIEWPORT, ...nextDocument.viewport });
    options.resetHistory();
    host.commit(nextDocument, `Imported ${file.name}`);
    host.applySelectionSnapshot(pageSwitchSelection(host, nextDocument.pages[0]!.id));
    selectionStore.setActiveTool("select");
    selectionStore.setSelectedShapeKind("rectangle");
    options.resetScopedState();
    await options.resolveFonts(nextDocument);
    options.notifyFontsResolved();
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not import .curva file");
  } finally {
    uiStore.setDocumentLoading(false);
  }
}

export async function importComponentsFromCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
  options: ComponentImportOptions,
): Promise<void> {
  if (!file || options.componentImportInProgress) {
    return;
  }

  const filename = file.name || "components.curva";
  uiStore.setComponentImportProgress({ phase: "reading", filename, current: 0, total: 1 });
  try {
    const imported = deserializeCurvaPackage(file.bytes, { importPreviews: false }).document;
    const pageName = `${sanitizeDocumentBaseName(filename)} components`;
    uiStore.setComponentImportProgress({ phase: "importing", filename, current: 0, total: 1 });

    const prepared = prepareComponentImport(documentStore.getDocument(), imported, pageName);
    if (prepared.componentIds.length === 0) {
      uiStore.setMessage(`No components found in ${filename}`);
      return;
    }

    const context = host.commandContext();
    const nextSelection = pageSwitchSelection(host, prepared.page.id);
    host.history.execute(new ImportComponentsPageCommand(prepared.page, prepared.styles), context, { afterSelection: nextSelection });
    host.commit(context.document, `Imported ${prepared.componentIds.length} component${prepared.componentIds.length === 1 ? "" : "s"}`);
    host.applySelectionSnapshot(nextSelection);

    const firstImportedComponent = findSceneNode(context.document, prepared.componentIds[0]!);
    if (firstImportedComponent) {
      options.centerViewportOnDocumentPoint(
        firstImportedComponent.bounds.x + firstImportedComponent.bounds.width / 2,
        firstImportedComponent.bounds.y + firstImportedComponent.bounds.height / 2,
      );
    }

    for (let index = 0; index < prepared.componentIds.length; index += 1) {
      uiStore.setComponentImportProgress({ phase: "previews", filename, current: index, total: prepared.componentIds.length });
      try {
        await options.refreshComponentThumbnail(context.document, prepared.componentIds[index]!);
      } catch {
        // Keep the component import usable even if one preview cannot be rendered.
      }
    }

    uiStore.setComponentImportProgress({ phase: "previews", filename, current: prepared.componentIds.length, total: prepared.componentIds.length });
    uiStore.setMessage(`Imported ${prepared.componentIds.length} component${prepared.componentIds.length === 1 ? "" : "s"} from ${filename}`);
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not import components");
  } finally {
    window.setTimeout(() => uiStore.setComponentImportProgress(undefined), 500);
  }
}

export async function importScriptsFromCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
): Promise<void> {
  if (!file) {
    return;
  }

  const filename = file.name || "scripts.curva";
  try {
    const imported = deserializeCurvaPackage(file.bytes, { importPreviews: false }).document;
    const prepared = prepareScriptImport(
      documentStore.getDocument(),
      imported,
      sanitizeDocumentBaseName(filename),
    );
    if (!prepared) {
      uiStore.setMessage(`No scripts found in ${filename}`);
      return;
    }

    host.runDocumentCommands([new ImportScriptsFolderCommand(prepared.folder)], "Imported scripts");
    uiStore.setMessage(
      `Imported ${prepared.scriptCount} script${prepared.scriptCount === 1 ? "" : "s"} into “${prepared.folder.name}”`,
    );
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not import scripts");
  }
}

export async function importAndReplaceScriptsFromCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
): Promise<void> {
  if (!file) {
    return;
  }

  const filename = file.name || "scripts.curva";
  try {
    const imported = deserializeCurvaPackage(file.bytes, { importPreviews: false }).document;
    const nextLibrary = prepareScriptLibraryReplace(imported);
    if (!nextLibrary) {
      uiStore.setMessage(`No scripts found in ${filename}`);
      return;
    }

    const scriptCount = listDocumentScripts(nextLibrary).length;
    host.runDocumentCommands([new ReplaceScriptLibraryCommand(nextLibrary)], "Replaced scripts");
    uiStore.setMessage(
      `Replaced scripts with ${scriptCount} script${scriptCount === 1 ? "" : "s"} from ${filename}`,
    );
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not replace scripts");
  }
}

export async function addEditorScriptsFromCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
): Promise<void> {
  if (!file) {
    return;
  }

  const filename = file.name || "scripts.curva";
  try {
    const imported = deserializeCurvaPackage(file.bytes, { importPreviews: false }).document;
    if (listDocumentScripts(imported.scripts).length === 0) {
      uiStore.setMessage(`No scripts found in ${filename}`);
      return;
    }

    host.runDocumentCommands(
      [new AddEditorScriptsCommand(imported, sanitizeDocumentBaseName(filename))],
      "Added editor scripts",
    );
    const count = listDocumentScripts(imported.scripts).length;
    uiStore.setMessage(
      `Added ${count} editor script${count === 1 ? "" : "s"} from ${filename}`,
    );
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not add editor scripts");
  }
}

export async function replaceEditorScriptsFromCurvaFile(
  host: EditorCommandHost,
  file: OpenedFile | undefined,
): Promise<void> {
  if (!file) {
    return;
  }

  const filename = file.name || "scripts.curva";
  try {
    const imported = deserializeCurvaPackage(file.bytes, { importPreviews: false }).document;
    if (listDocumentScripts(imported.scripts).length === 0) {
      uiStore.setMessage(`No scripts found in ${filename}`);
      return;
    }

    imported.name = EDITOR_SCRIPTS_DOCUMENT_NAME;
    host.runDocumentCommands(
      [new ReplaceEditorScriptsCommand(imported)],
      "Replaced editor scripts",
    );
    const count = listDocumentScripts(imported.scripts).length;
    uiStore.setMessage(
      `Replaced editor scripts with ${count} script${count === 1 ? "" : "s"} from ${filename}`,
    );
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not replace editor scripts");
  }
}

export function clearEditorScripts(host: EditorCommandHost): void {
  host.runDocumentCommands([new ClearEditorScriptsCommand()], "Cleared editor scripts");
  uiStore.setMessage("Cleared editor scripts");
}

export function copyEditorScriptEntryToDocument(host: EditorCommandHost, entryId: string): void {
  try {
    host.runDocumentCommands(
      [new CopyEditorScriptEntryToDocumentCommand(entryId)],
      "Copied script to document",
    );
    uiStore.setMessage("Copied script to document");
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not copy script to document");
  }
}

/**
 * Export a copy of the editor-scripts package.
 * Browser: download. Native: Save As dialog (does not change the AppConfig path).
 */
export async function downloadEditorScriptsDocument(
  _host: EditorCommandHost,
  document: CurvaDocument,
): Promise<void> {
  try {
    const exportDocument = {
      ...document,
      name: document.name || EDITOR_SCRIPTS_DOCUMENT_NAME,
    };
    const filename = normalizedCurvaFileName(exportDocument.name);
    let documentThumbnailPng: Uint8Array | undefined;
    try {
      documentThumbnailPng = await renderDocumentThumbnailPngBytes(exportDocument);
    } catch {
      // Export still succeeds without a Finder preview.
    }
    const result = await saveDocumentBytes(
      serializeCurvaPackage(exportDocument, { documentThumbnailPng }),
      {
        filename,
        mimeType: CURVA_MIME_TYPE,
        // Always prompt / download — never overwrite the AppConfig editor-scripts file.
        path: undefined,
      },
    );
    if (result.method === "cancelled") {
      uiStore.setMessage("Download cancelled");
      return;
    }
    if (result.method === "write") {
      const savedName = result.path?.split(/[/\\]/).at(-1) ?? filename;
      uiStore.setMessage(`Saved copy ${savedName}`);
      return;
    }
    uiStore.setMessage(`Downloaded ${filename}`);
  } catch (error) {
    uiStore.setMessage(
      error instanceof Error ? error.message : "Could not download editor scripts document",
    );
  }
}

export interface CurvaSaveOptions {
  /** Existing filesystem path for Save (native). Omit / undefined triggers Save As dialog. */
  path?: string;
}

export async function exportCurvaFile(
  host: EditorCommandHost,
  options: CurvaSaveOptions = {},
): Promise<string | undefined> {
  try {
    const document = documentStore.getDocument();
    const exportDocument = host.documentWithCurrentViewport(document);
    const filename = normalizedCurvaFileName(exportDocument.name);
    let documentThumbnailPng: Uint8Array | undefined;
    try {
      documentThumbnailPng = await renderDocumentThumbnailPngBytes(exportDocument);
    } catch {
      // Export still succeeds without a Finder preview.
    }
    const result = await saveDocumentBytes(
      serializeCurvaPackage(exportDocument, { documentThumbnailPng }),
      {
        filename,
        mimeType: CURVA_MIME_TYPE,
        path: options.path,
      },
    );
    if (result.method === "cancelled") {
      uiStore.setMessage("Save cancelled");
      return undefined;
    }
    host.history.markSaved(document.revision);
    if (result.method === "write") {
      const savedName = result.path?.split(/[/\\]/).at(-1) ?? filename;
      uiStore.setMessage(`Saved ${savedName}`);
      return result.path;
    }
    uiStore.setMessage(`Exported ${filename}`);
    return undefined;
  } catch (error) {
    const detail =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : error && typeof error === "object" && "message" in error && typeof (error as { message: unknown }).message === "string"
            ? (error as { message: string }).message
            : undefined;
    uiStore.setMessage(detail ? `Could not save .curva file: ${detail}` : "Could not save .curva file");
    return undefined;
  }
}

export async function exportSelectedFramesPdf(host: EditorCommandHost): Promise<void> {
  const document = documentStore.getDocument();
  const frames = host.currentSelection().selectedNodeIds.flatMap(nodeId => {
    const node = findSceneNode(document, nodeId);
    return node?.type === "frame" ? [node] : [];
  });
  if (frames.length === 0) {
    uiStore.setMessage("Select one or more frames to export as PDF");
    return;
  }

  let finalizationTimer: number | undefined;
  try {
    uiStore.setMessage(`Exporting ${frames.length} frame${frames.length === 1 ? "" : "s"} as PDF…`);
    const blob = await exportFramesToPdfBlob(document, frames, {
      onProgress: progress => {
        if (progress.phase === "rendering") {
          uiStore.setMessage(`Exporting PDF: ${progress.frameName} (${progress.current}/${progress.total})`);
          return;
        }

        const startedAt = performance.now();
        uiStore.setMessage(`Finalizing ${progress.total}-page PDF…`);
        finalizationTimer = window.setInterval(() => {
          const elapsedSeconds = Math.max(1, Math.round((performance.now() - startedAt) / 1000));
          uiStore.setMessage(`Finalizing ${progress.total}-page PDF… ${elapsedSeconds}s`);
        }, 1_000);
      },
    });
    if (finalizationTimer !== undefined) {
      window.clearInterval(finalizationTimer);
      finalizationTimer = undefined;
    }
    uiStore.setMessage("Preparing PDF download…");
    const filename = frames.length === 1
      ? `${normalizedExportBaseName(frames[0]!.name)}.pdf`
      : `${normalizedExportBaseName(document.name)}-frames.pdf`;
    await exportBytes(new Uint8Array(await blob.arrayBuffer()), {
      filename,
      mimeType: "application/pdf",
    }).then(result => {
      if (result.method === "cancelled") {
        uiStore.setMessage("PDF export cancelled");
        return;
      }
      uiStore.setMessage(`Exported ${frames.length} frame${frames.length === 1 ? "" : "s"} as PDF`);
    });
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not export PDF");
  } finally {
    if (finalizationTimer !== undefined) {
      window.clearInterval(finalizationTimer);
    }
  }
}

export function switchPage(host: EditorCommandHost, pageId: string): void {
  const document = documentStore.getDocument();
  if (pageId === host.currentSelection().activePageId) {
    return;
  }
  const page = document.pages.find(candidate => candidate.id === pageId);
  if (!page) {
    return;
  }
  host.updateSelection(pageSwitchSelection(host, pageId), true);
  uiStore.setContextMenu(undefined);
  uiStore.setMessage(`Switched to ${page.name}`);
}

export function addPage(host: EditorCommandHost): void {
  const context = host.commandContext();
  const page = createPage(nextPageName(context.document));
  const nextSelection = pageSwitchSelection(host, page.id);
  host.history.execute(new AddPageCommand(page), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, `Added ${page.name}`);
}

export function renamePage(host: EditorCommandHost, pageId: string, name: string): void {
  const nextName = name.trim();
  const page = documentStore.getDocument().pages.find(candidate => candidate.id === pageId);
  if (!page || nextName.length === 0 || page.name === nextName) {
    return;
  }
  const context = host.commandContext();
  host.history.execute(new RenamePageCommand(pageId, page.name, nextName), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Renamed page");
}

export function deletePage(host: EditorCommandHost, pageId: string): void {
  const document = documentStore.getDocument();
  if (document.pages.length <= 1) {
    uiStore.setMessage("Cannot delete the last page");
    return;
  }
  const context = host.commandContext();
  const activePageId = host.currentSelection().activePageId;
  const nextActivePageId = activePageId === pageId
    ? fallbackActivePageIdAfterDelete(context.document, pageId)
    : activePageId;
  const nextSelection = nextActivePageId ? pageSwitchSelection(host, nextActivePageId) : host.currentSelection();
  host.history.execute(new DeletePageCommand(pageId), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  uiStore.setContextMenu(undefined);
  host.commit(context.document, "Deleted page");
}

export function duplicatePage(host: EditorCommandHost, pageId: string): void {
  const sourcePage = documentStore.getDocument().pages.find(candidate => candidate.id === pageId);
  if (!sourcePage) {
    return;
  }
  const context = host.commandContext();
  const duplicated = clonePage(sourcePage, duplicatePageName(context.document, sourcePage.name));
  const nextSelection = pageSwitchSelection(host, duplicated.id);
  host.history.execute(new DuplicatePageCommand(pageId, duplicated), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, `Duplicated ${sourcePage.name}`);
}

export function addGuide(host: EditorCommandHost, scope: GuideScope, guide: Guide, options: GuideHandlerOptions = {}): void {
  const context = host.commandContext();
  host.history.execute(new AddGuideCommand(scope, guide), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Added guide");
  setSelectedGuide({ id: guide.id, scope }, options);
}

export function moveGuide(
  host: EditorCommandHost,
  selection: GuideSelection,
  fromPosition: number,
  toPosition: number,
  options: GuideHandlerOptions = {},
): void {
  const context = host.commandContext();
  host.history.execute(new MoveGuideCommand(selection, fromPosition, toPosition), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Moved guide");
  setSelectedGuide(selection, options);
}

export function relocateGuide(
  host: EditorCommandHost,
  selection: GuideSelection,
  fromPosition: number,
  toScope: GuideScope,
  toPosition: number,
  options: GuideHandlerOptions = {},
): void {
  const context = host.commandContext();
  host.history.execute(new RelocateGuideCommand(selection, fromPosition, toScope, toPosition), context, { afterSelection: host.currentSelection() });
  setSelectedGuide({ id: selection.id, scope: toScope }, options);
  host.commit(context.document, "Moved guide");
}

export function deleteGuide(host: EditorCommandHost, selection: GuideSelection, options: GuideHandlerOptions = {}): void {
  const context = host.commandContext();
  host.history.execute(new DeleteGuideCommand(selection), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Deleted guide");
  const currentGuide = uiStore.selectedGuide.getValue();
  setSelectedGuide(currentGuide?.id === selection.id ? undefined : currentGuide, options);
}

export function updateSelectedGuide(selection: GuideSelection | undefined, options: GuideHandlerOptions = {}): void {
  setSelectedGuide(selection, options);
}

function setSelectedGuide(selection: GuideSelection | undefined, options: GuideHandlerOptions): void {
  uiStore.setSelectedGuide(selection);
  options.onSelectedGuideChange?.(selection);
}

function sanitizeDocumentBaseName(name: string): string {
  return name
    .replace(new RegExp(`${escapeRegExp(CURVA_FILE_EXTENSION)}$`, "i"), "")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim() || "Untitled";
}

function normalizedCurvaFileName(name: string): string {
  return `${sanitizeDocumentBaseName(name)}${CURVA_FILE_EXTENSION}`;
}

import { normalizedExportBaseName } from "@/editor/shell/shellExportNames";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
